import React, { useRef, useState, useCallback, useEffect, useImperativeHandle } from 'react';
import type { PlayerActionsBarProps, PlayerActionsBarAPI, ProxyInfo, SourceFileInfo } from './types';

declare const $: any;

/**
 * Renders an <a href="#"> that carries a native `onclick` DOM attribute.
 *
 * We can't use React's `onClick` here because these links live inside <ul>
 * menus that are jQuery-cloned by `showMenuForButton()`. jQuery `.clone()`
 * preserves native DOM attributes but drops React synthetic event handlers.
 * React also rejects lowercase `onclick` as a JSX prop, so we set it via
 * a ref after mount.
 */
function MenuAction({ handler, children }: { handler: string; children: React.ReactNode }) {
  const ref = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.setAttribute('onclick', handler);
    }
  }, [handler]);
  return <a href="#" ref={ref}>{children}</a>;
}

function buildDownloadUrl(fileId: number, linkId?: string | null, proxyId?: number): string {
  let url = 'download?';
  if (linkId) {
    url += `link=${linkId}&id=${fileId}`;
  } else {
    url += `id=${fileId}`;
  }
  if (proxyId != null) {
    url += `&proxy_id=${proxyId}`;
  }
  return url;
}

/** Show "{width}x{height} {EXT}" for video/image, just "{EXT}" for everything else. */
function proxyLabel(proxy: ProxyInfo, fileTypeId: number): string {
  const ext = proxy.fileExtension || 'Unknown';
  if ((fileTypeId === 1 || fileTypeId === 3) && proxy.width > 0 && proxy.height > 0) {
    return `${proxy.width}x${proxy.height} ${ext}`;
  }
  return ext;
}

function sourceLabel(sourceFile: SourceFileInfo, fileTypeId: number): { name: string; prefix: string } {
  const ext = sourceFile.fileExtension || 'Unknown';
  let prefix = '';
  let name = ext;
  if ((fileTypeId === 1 || fileTypeId === 3) && sourceFile.width > 0 && sourceFile.height > 0) {
    prefix = `${sourceFile.width}x${sourceFile.height}`;
    name = `${prefix} ${ext}`;
  }
  return { name, prefix };
}

const PlayerActionsBar = React.forwardRef<PlayerActionsBarAPI, PlayerActionsBarProps>(
  function PlayerActionsBar(props, ref) {
    const {
      fileId,
      colId,
      fileTypeId,
      linkId,
      departmentId,
      parentPath,
      aliasTo = -1,
      canDownload = true,
      proxies,
      sourceFile,
      canShare = true,
      isSubscribed: initialSubscribed,
      isFavorited: initialFavorited,
      currentWorkflowId,
      workflows,
      isOwnerOrAdmin,
      hasProxy,
      canEncode,
      isBeingConverted,
      hideDownloadButton = false,
      hideShareButton = false,
      hideSubscribeButton = false,
      hideFavoriteButton = false,
      hideActionsButton = false,
    } = props;

    const isLink = linkId != null;
    const containerRef = useRef<HTMLDivElement>(null);
    const downloadBtnRef = useRef<HTMLButtonElement>(null);
    const downloadMenuRef = useRef<HTMLUListElement>(null);
    const shareBtnRef = useRef<HTMLButtonElement>(null);
    const shareMenuRef = useRef<HTMLUListElement>(null);
    const actionsBtnRef = useRef<HTMLButtonElement>(null);
    const actionsMenuRef = useRef<HTMLUListElement>(null);

    const [subscribed, setSubscribed] = useState(initialSubscribed);
    const [favorited, setFavorited] = useState(initialFavorited);
    /** Dimensions of the currently playing video source — used to label the
     *  Current Frame JPEG/PNG download options. Updates when the source
     *  changes (e.g. file switch, quality switch). */
    const [frameSize, setFrameSize] = useState<{ width: number; height: number } | null>(null);

    // Reset local state when props change (file switch)
    useEffect(() => {
      setSubscribed(initialSubscribed);
      setFavorited(initialFavorited);
    }, [initialSubscribed, initialFavorited]);

    // Track the active video element's frame dimensions.
    //
    // Post multi-column refactor: the <video> no longer has a fixed
    // `media-player` id — each PlayerSlot uses React's useId(). Scope the
    // lookup to this column's `.player-stage` via the containerRef's closest
    // `.player-column` ancestor so we don't grab another column's video.
    useEffect(() => {
      if (fileTypeId !== 1) return;
      const column = containerRef.current?.closest('.player-column');
      const video =
        (column?.querySelector('.player-stage video') as HTMLVideoElement | null)
        // Fallback for test/legacy environments without the column wrapper.
        ?? (document.querySelector('.player-stage video') as HTMLVideoElement | null);
      if (!video) return;

      const update = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          setFrameSize({ width: video.videoWidth, height: video.videoHeight });
        }
      };

      update();
      video.addEventListener('loadedmetadata', update);
      video.addEventListener('loadeddata', update);
      return () => {
        video.removeEventListener('loadedmetadata', update);
        video.removeEventListener('loadeddata', update);
      };
    }, [fileId, fileTypeId]);

    // Keep window.fileID in sync
    useEffect(() => {
      (window as any).fileID = fileId;
    }, [fileId]);

    useImperativeHandle(ref, () => ({
      getContainerElement: () => containerRef.current,
    }), []);

    const showMenu = useCallback((btnRef: React.RefObject<HTMLElement | null>, menuRef: React.RefObject<HTMLElement | null>) => {
      if (btnRef.current && menuRef.current && typeof (window as any).showMenuForButton === 'function') {
        (window as any).showMenuForButton($(btnRef.current), $(menuRef.current));
      }
    }, []);

    const handleDownloadClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      showMenu(downloadBtnRef, downloadMenuRef);
    }, [showMenu]);

    const handleShareClick = useCallback((e: React.MouseEvent) => {
      e.preventDefault();
      showMenu(shareBtnRef, shareMenuRef);
    }, [showMenu]);

    const handleActionsClick = useCallback(() => {
      showMenu(actionsBtnRef, actionsMenuRef);
    }, [showMenu]);

    const handleSubscribe = useCallback(() => {
      setSubscribed(prev => !prev);
      $.post('ajax/watch', { file: fileId }).fail(function (_jqXHR: any, _textStatus: string, errorString: string) {
        alert('Failed to subscribe to file: ' + errorString);
        setSubscribed(prev => !prev); // Revert on failure
      });
    }, [fileId]);

    const handleFavorite = useCallback(() => {
      setFavorited(prev => !prev);
      $.post('ajax/favorites.php', { toggle: fileId, type: 0 }).fail(function (_jqXHR: any, _textStatus: string, errorString: string) {
        alert('Failed to set favorite: ' + errorString);
        setFavorited(prev => !prev); // Revert on failure
      });
    }, [fileId]);

    // NOTE: Menu items inside <ul> menus use native `onclick` string attributes
    // instead of React's `onClick` because `showMenuForButton()` jQuery-clones
    // the <ul> before displaying it. jQuery `.clone()` preserves native DOM
    // attributes but drops React synthetic event handlers.

    // Build download URL base
    const baseDownloadUrl = buildDownloadUrl(fileId, linkId);
    const showDownloadButton = canDownload && fileTypeId !== 5 && !hideDownloadButton;
    const hasDownloadMenu = proxies.length > 0 || fileTypeId === 1;
    const source = sourceLabel(sourceFile, fileTypeId);
    const encodeTargetId = aliasTo > -1 ? aliasTo : fileId;

    const frameDownloadJPEG = `downloadVideoFrame('image/jpeg', '${colId}')`;
    const frameDownloadPNG = `downloadVideoFrame('image/png', '${colId}')`;

    return (
      <div id="player-actions-container" ref={containerRef}>
        <div id="player-actions-sizer">

          {/* Download */}
          {showDownloadButton && hasDownloadMenu && (
            <>
              <a href="#" onClick={handleDownloadClick} title="Download">
                <button className="player-action" id="player-action-download" ref={downloadBtnRef} type="button">
                  <div className="fa-icon fa-icon-download-cloud"></div> Download &#x25BE;
                </button>
              </a>
              <ul id="download-menu" ref={downloadMenuRef} style={{ display: 'none' }}>
                <li key="heading" className="ui-menu-heading">File</li>
                {proxies.map(proxy => (
                  <li key={`proxy-${proxy.proxyId}`}>
                    <a href={buildDownloadUrl(fileId, linkId, proxy.proxyId)}>
                      <div className="download-file-icon"></div> {proxyLabel(proxy, fileTypeId)} (Proxy)
                    </a>
                  </li>
                ))}
                <li key="original">
                  <a href={baseDownloadUrl}>
                    <div className="download-file-icon"></div> {source.name}{proxies.length > 0 ? ' (Original)' : ''}
                  </a>
                </li>
                {fileTypeId === 1 && (
                  <React.Fragment key="frame-capture">
                    <li key="frame-heading" className="ui-menu-heading">Current Frame</li>
                    <li key="frame-jpeg"><MenuAction handler={frameDownloadJPEG}>{frameSize ? `${frameSize.width}x${frameSize.height} JPEG` : 'JPEG'}</MenuAction></li>
                    <li key="frame-png"><MenuAction handler={frameDownloadPNG}>{frameSize ? `${frameSize.width}x${frameSize.height} PNG` : 'PNG'}</MenuAction></li>
                  </React.Fragment>
                )}
              </ul>
            </>
          )}

          {showDownloadButton && !hasDownloadMenu && (
            <a href={baseDownloadUrl} title="Download">
              <button className="player-action" type="button">
                <div className="fa-icon fa-icon-download-cloud"></div> Download
              </button>
            </a>
          )}

          {/* Share */}
          {!isLink && canShare && !hideShareButton && (
            <>
              <a href="#" onClick={handleShareClick} title="Share File">
                <button id={`player-share-${fileId}`} className="player-action" ref={shareBtnRef} type="button">
                  <div className="fa-icon fa-icon-link"></div> Share &#x25BE;
                </button>
              </a>
              <ul id="share-menu" ref={shareMenuRef} style={{ display: 'none' }}>
                <li key="create-link"><MenuAction handler={`shareFileID(${fileId}, $('#player-share-${fileId}'))`}>Create New Link...</MenuAction></li>
                <li key="view-links"><a href={`share?filter_file_id=${fileId}`}>View Existing Links...</a></li>
              </ul>
            </>
          )}

          {/* Subscribe */}
          {!isLink && !hideSubscribeButton && (
            <button
              className={`player-action nomobile${subscribed ? ' subscribed' : ''}`}
              id="player-subscribe"
              type="button"
              onClick={handleSubscribe}
            >
              <div className="fa-icon"></div> Subscribe
            </button>
          )}

          {/* Favorite */}
          {!isLink && !hideFavoriteButton && (
            <button
              className={`player-action${favorited ? ' subscribed' : ''}`}
              id="player-favorite"
              type="button"
              onClick={handleFavorite}
            >
              <div className="fa-icon"></div> Favorite
            </button>
          )}

          {/* Actions gear menu */}
          {!isLink && !hideActionsButton && (
            <>
              <button className="player-action" id="player-actions" ref={actionsBtnRef} type="button" onClick={handleActionsClick}>
                <div className="fa-icon fa-icon-gear"></div> Actions &#x25BE;
              </button>
              <ul id="player-actions-menu" ref={actionsMenuRef} style={{ display: 'none', fontSize: '17px' }} className="menu">
                {/* File operations */}
                <li key="upload-version"><a href={`upload?rev=${fileId}&dept=${departmentId}&parent=${encodeURIComponent(parentPath)}`}>Upload New Version...</a></li>
                <li key="replace"><a href={`upload?replace=${fileId}`}>Replace File...</a></li>
                <li key="metadata"><a href={`metadata?edit=${fileId}&redir=${encodeURIComponent(`player?id=${fileId}`)}`}>Edit Metadata</a></li>

                {/* Encoding */}
                {canEncode && !isBeingConverted && (
                  <li key="encode"><a href={`encode.php?file=${encodeTargetId}`}>Create playable versions</a></li>
                )}

                {/* Remove original/playable */}
                {isOwnerOrAdmin && hasProxy && (
                  <React.Fragment key="remove-files">
                    <li key="remove-original"><MenuAction handler={`removeOriginalFile('${fileId}')`}>Remove original file...</MenuAction></li>
                    <li key="remove-playable"><MenuAction handler={`removePlayableFile('${fileId}')`}>Remove all playable copies...</MenuAction></li>
                  </React.Fragment>
                )}

                {/* Separator */}
                <li key="sep">-</li>

                {/* Import */}
                <li key="import">
                  <a href="#">Import</a>
                  <ul>
                    <li key="import-comments"><a href={`import?id=${fileId}`}>Comments...</a></li>
                    <li key="import-metadata"><a href={`import_metadata?id=${fileId}`}>Metadata...</a></li>
                    <li key="import-captions"><a href={`import_captions?id=${fileId}`}>Captions...</a></li>
                    <li key="import-copy"><a href={`import_from_file?id=${fileId}`}>Copy from another file...</a></li>
                  </ul>
                </li>

                {/* Export */}
                <li key="export">
                  <a href="#">Export</a>
                  <ul>
                    <li key="export-comments"><MenuAction handler="showMarkerExportDialog()">Comments...</MenuAction></li>
                    <li key="export-metadata"><a href={`export?metadata=${fileId}`}>Metadata</a></li>
                    <li key="export-captions"><MenuAction handler="showSubtitleExportDialog()">Captions...</MenuAction></li>
                  </ul>
                </li>

                {/* Thumb / Metadata */}
                {isOwnerOrAdmin && fileTypeId > 0 && (fileTypeId < 4 || fileTypeId === 6) && (
                  <React.Fragment key="admin-actions">
                    <li key="thumb">
                      <MenuAction handler={`changeThumbToCurrent('${aliasTo > -1 ? aliasTo : fileId}', '${colId}')`}>
                        {fileTypeId === 1 ? 'Set Thumb to Current Frame' : 'Regenerate Thumbnails'}
                      </MenuAction>
                    </li>
                    <li key="regen-meta"><MenuAction handler={`gatherFileMetadata('${aliasTo > -1 ? aliasTo : fileId}')`}>Regenerate File Metadata</MenuAction></li>
                  </React.Fragment>
                )}
              </ul>
            </>
          )}
        </div>
      </div>
    );
  }
);

export default PlayerActionsBar;
